import React, { useState } from "react";
import { X, ChevronDown } from "lucide-react";

interface SelectedSpacesDrawerProps {
  selectedSpacesChips: string[];
  onRemoveSpaceChip: (spaceId: string) => void;
  onClearAllSpaceChips: () => void;
  plansInfo?: any;
}

const SelectedSpacesDrawer: React.FC<SelectedSpacesDrawerProps> = ({
  selectedSpacesChips,
  onRemoveSpaceChip,
  onClearAllSpaceChips,
  plansInfo,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // If selectedSpacesChips is undefined, default to empty array
  const chips = selectedSpacesChips || [];
  const hasChips = chips.length > 0;

  const getSpaceLabel = (id: string) => {
    if (!plansInfo?.spaces) return id;
    
    const space = plansInfo.spaces.find(
      (s: any) => 
        s.spaceNumber?.toLowerCase() === id.toLowerCase() || 
        s.spaceNumber?.replaceAll("_", " ").toLowerCase() === id.replaceAll("_", " ").toLowerCase()
    );
    
    if (space && space.spaceHeader && space.spaceHeader !== space.spaceNumber) {
      return `${id} - ${space.spaceHeader}`;
    }
    return id;
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        title={!hasChips ? "Use tools to Select Spaces" : ""}
        className={`flex items-center gap-1.5 text-slate-600 font-medium transition py-1 px-2 rounded-md hover:text-blue-600 hover:bg-slate-50 ${isOpen ? 'bg-blue-50 text-blue-600' : ''}`}
      >
        <span className="flex items-center justify-center text-white text-[10px] font-bold h-4 min-w-[16px] px-1 rounded-full shadow-sm bg-blue-600">
          {chips.length}
        </span>
        <span className="whitespace-nowrap">Spaces Selected</span>
        <ChevronDown size={12} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setIsOpen(false)}></div>
          <div className="absolute top-full right-0 mt-1 w-64 bg-white rounded-lg shadow-xl border border-slate-100 py-2 px-3 z-40 animate-in fade-in zoom-in-95 duration-100">
            {!hasChips ? (
              <div className="py-2 flex items-center justify-center text-xs font-medium text-blue-600 bg-blue-50 rounded-md border border-blue-100">
                Use tools to select spaces
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-100">
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                    Selected Spaces
                  </span>
                  <button
                    onClick={() => {
                      onClearAllSpaceChips();
                      setIsOpen(false);
                    }}
                    className="text-[10px] text-red-500 font-semibold hover:text-red-700 hover:underline"
                  >
                    Clear All
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto pr-1">
                  {chips.map((id, index) => (
                    <div
                      key={`${id}-${index}`}
                      className="flex-shrink-0 flex items-center gap-1 bg-white border border-blue-100 text-blue-600 px-1.5 py-0.5 rounded text-[10px] font-mono shadow-sm"
                    >
                      {getSpaceLabel(id)}
                      <button
                        onClick={() => onRemoveSpaceChip(id)}
                        className="hover:text-red-500"
                      >
                        <X size={8} />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SelectedSpacesDrawer;
